// THE CUSTOMER'S PO, TYPED IN, COMPARED AGAINST THIS QUOTE.
//
// ⚠️ NOBODY READS THE DOCUMENT. The lines here are TYPED BY A PERSON, and the
// panel says so in as many words. That sentence is the most important thing on
// this screen: an upload panel sitting beside a comparison panel invites the
// reading that the system checked the PDF, and a verdict somebody believes was
// read off their document -- when it was read off what a colleague typed -- is
// worse than no verdict at all.
//
// ⚠️ THE VERDICTS COME FROM THE SERVER. One pure module decides them
// (addons/anixify_cpq/models/po_comparison.py), so there is exactly one
// implementation of the decision rather than one per client. This component
// renders what it is told and computes nothing.
//
// ⚠️ AND THE COMPARISON CHANGES NOTHING. No quote, no line, no price, no
// state, no order. It is an opinion about two lists.
//
// ⚠️ A RECORDED PO LOADS BACK INTO THE FORM. Without it a stored PO was
// reachable only while its lines were still on screen: reload the page and it
// had to be retyped to be revised -- the same "usable once, then blocked"
// shape as the empty-state Add Discount button and the drafts missing from
// the quote list. LOADING IS READ-ONLY: it fills the form from the list read
// this component already makes, and nothing is written until somebody presses
// Save. PoComparison.load.test.tsx and QuoteDetail.poLoad.test.tsx both prove
// no save, delete or compare request fires on load.
//
// ROLES, via the existing helper: read is everyone who can see the quote,
// write is useCanEditQuotes() -- Sales or Admin. The backend enforces both.

import { useCallback, useEffect, useRef, useState } from "react";
import { ClipboardList, ClipboardPaste, FileSearch, Plus, Trash2 } from "lucide-react";
import {
  listQuotePos, saveQuotePo, compareQuotePo, deleteQuotePo,
  type QuotePo, type PoComparison as PoComparisonResult, type PoVerdict,
  extractPoDocument, listQuoteDocuments,
  type ExtractResult, type QuoteDocument, type ExtractedHeader, type ExtractedRow,
  type PoHeaderInput, api, listCurrencies, setQuoteShipTo,
  DEFAULT_SUPPLIER_CODE_LABEL, entityAliasList,
} from "@/services/api";
import type { QuoteOutcome } from "@/types";
import {
  PoHeaderFields, PoWarningStrip, EMPTY_PO_HEADER, type PoHeaderDraft,
} from "@/components/PoHeaderFields";
// ⚠️ THE WARNING DECISIONS ARE NOT IN THIS FILE. One pure module owns them,
// so there is one implementation to test and this component renders what it
// is told -- the same split as po_comparison.py deciding the verdicts.
import {
  poHeaderWarnings, shipmentsNote, currencyRecognition, type PoWarning,
} from "@/services/poWarnings";
import { useCanEditQuotes } from "@/auth/AuthContext";
import {
  parsePastedPoLines, UNREADABLE_LABEL,
  type ParsedRow, type ParseResult,
} from "@/services/poPaste";
import { formatRupees } from "@/lib/format";
import {
  colors, semanticColors, radius, spacing, heights, typography,
  iconSize, iconStroke,
} from "@/styles/tokens";

/** How each verdict reads and looks.
 *
 *  ⚠️ UNMATCHED AND AMBIGUOUS ARE NOT FAILURES OF THE USER, and the wording
 *  carries that. "Not on the quote" states a fact about two documents; "we
 *  could not tell" owns the ambiguity as the system's. Nothing here says
 *  "invalid", "error" or "wrong" -- a salesperson reading this is comparing
 *  their customer's paperwork, not being marked.
 *
 *  Colour separates the three kinds without ranking the people involved:
 *  agreement is green, a stated difference is amber, and a refusal to guess is
 *  indigo -- the informational tone, because it is a question, not a problem. */
const VERDICT_STYLE: Record<PoVerdict, { label: string; fg: string; bg: string; border: string }> = {
  matched: {
    label: "Matches",
    fg: colors.green600, bg: colors.green50, border: colors.green100,
  },
  quantity_mismatch: {
    label: "Quantity differs",
    fg: semanticColors.warningTextOnTint, bg: semanticColors.warningBg,
    border: semanticColors.warningBorder,
  },
  price_mismatch: {
    label: "Unit price differs",
    fg: semanticColors.warningTextOnTint, bg: semanticColors.warningBg,
    border: semanticColors.warningBorder,
  },
  part_not_on_quote: {
    label: "Not on the quote",
    fg: semanticColors.warningTextOnTint, bg: semanticColors.warningBg,
    border: semanticColors.warningBorder,
  },
  on_quote_not_on_po: {
    label: "Quoted, not on the PO",
    fg: semanticColors.textSecondary, bg: semanticColors.panelBg,
    border: semanticColors.border,
  },
  ambiguous: {
    label: "We could not tell which line",
    fg: semanticColors.accentTextOnTint, bg: colors.indigo50, border: colors.indigo100,
  },
};

function VerdictPill({ verdict }: { verdict: PoVerdict }) {
  const s = VERDICT_STYLE[verdict];
  return (
    <span
      data-testid={`po-verdict-${verdict}`}
      data-verdict={verdict}
      style={{
        display: "inline-flex", alignItems: "center",
        height: heights.pill, padding: `0 ${spacing[8]}px`,
        borderRadius: radius.base,
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.medium,
        color: s.fg, backgroundColor: s.bg, border: `1px solid ${s.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
}

/** WHERE A SAVED LINE'S DIGITS CAME FROM. Display only.
 *
 *  ⚠️ IT READS `line_origin` AND NOTHING ELSE. That column is written by the
 *  backend, on the confirm path only, and cannot be set from a payload -- so
 *  it is the one piece of evidence in the system about who originated a
 *  figure. Rendering anything else here (the local `from_extraction` flag, a
 *  guess from which panel the row came out of) would put a claim on screen
 *  that no record backs.
 *
 *  ⚠️ ABSENT IS NOT "MANUAL", AND NEITHER IS IT "EXTRACTED". A response that
 *  omits the field, or a row saved before the column existed, shows NO TAG --
 *  the honest reading of an unknown. Only the exact string "extracted" earns
 *  one, so a future third value is silently ignored rather than mislabelled.
 *
 *  ⚠️ NEUTRAL, NOT A STATUS. Both origins were confirmed by a person; the
 *  difference is who typed the digits. Grey, per the pill convention -- green
 *  would read as verified and amber as suspect, and it is neither.
 *
 *  ⚠️ AND IT BLOCKS NOTHING. A span beside an input. The line stays editable
 *  and savable exactly as before; a test asserts that rather than trusting it.
 */
export function LineOriginTag({ origin, testId }: {
  origin?: string | null;
  testId: string;
}) {
  if (origin !== "extracted") return null;
  return (
    <span
      data-testid={testId}
      data-line-origin="extracted"
      // Says what it means on hover, because "Read from PDF" alone could be
      // read as "nobody checked it". Somebody did -- that is what Confirm is.
      title="A person confirmed this line after the reader read it out of the PDF."
      style={{
        display: "inline-flex", alignItems: "center",
        height: heights.pill, padding: `0 ${spacing[8]}px`,
        borderRadius: radius.base,
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.medium,
        color: semanticColors.textSecondary,
        backgroundColor: semanticColors.panelBg,
        border: `1px solid ${semanticColors.border}`,
        whiteSpace: "nowrap", flexShrink: 0,
      }}
    >
      Read from PDF
    </span>
  );
}

/** One form row. `description` and `manufacturer` have no input on this form
 *  and are CARRIED rather than shown.
 *
 *  ⚠️ THAT IS WHAT MAKES A LOADED PO SAFE TO RE-SAVE. A re-save REPLACES the
 *  PO's lines wholesale, so a field the form does not hold would be erased by
 *  the first save after a load -- silently, on data somebody typed. Carrying
 *  them through the row means a loaded line goes back exactly as it came,
 *  apart from whatever the person actually changed. */
type Draft = {
  part_number: string;
  quantity: string;
  unit_price: string;
  manufacturer: string;
  description: string;
  /** ⚠️ WHERE THIS ROW'S DIGITS CAME FROM, AND IT MUST RIDE THE SAVE PAYLOAD.
   *  The backend refuses to record line_origin "extracted" unless the row
   *  says `from_extraction: true` -- so dropping this flag anywhere between
   *  the review table and onSave saves every extracted row as "manual", which
   *  is a silent lie about who read the number. A test asserts it end to end,
   *  not merely that the field exists. */
  from_extraction?: boolean;
  // --- CARRIED, exactly like manufacturer and description above, and for
  // the same reason: a re-save REPLACES the PO's lines wholesale, so a field
  // the form does not hold would be erased by the first save after a load.
  //
  // ⚠️ `unit_price_text` IS NOT A SECOND PRICE. `unit_price` is the figure
  // arithmetic uses; this is the document's own typography, kept because
  // Oracle prints four decimals and the screen must show every one of them.
  // ⚠️ `extended_price` IS A PRINTED FACT AND IS NEVER COMPUTED. A line whose
  // document printed no extended price carries none -- deriving it from
  // quantity x unit price would put a figure on the record that appears on no
  // page of the customer's PO.
  line_number?: string;
  uom?: string;
  unit_price_text?: string;
  extended_price?: string;
  extended_price_text?: string;
  /** ⚠️ NEVER the part number and never matched on. */
  anixter_part_number?: string;
  /** Why the document printed no single quantity for this line. */
  shipments_note?: string;
  /** ⚠️ WHAT THE BACKEND RECORDED, FOR DISPLAY, AND IT IS NEVER SENT.
   *  `line_origin` is decided by the code path and refused from a payload, so
   *  echoing it back would at best be ignored and at worst look like a claim
   *  this form is entitled to make. It rides the Draft only so a loaded row
   *  can show its tag; `onSave` builds its payload from named fields and
   *  never spreads a Draft, which is what keeps it off the wire. */
  line_origin?: string | null;
};

const EMPTY_ROW: Draft = {
  part_number: "", quantity: "", unit_price: "", manufacturer: "", description: "",
};

/** A stored PO's header as the form edits it. Absent values become "" so an
 *  input can hold them; `headerHasAnything` is what decides whether the panel
 *  is shown at all, so a typed PO gets no empty header block. */
function headerFromPo(po: QuotePo): PoHeaderDraft {
  const t = (v: string | null | undefined) => (v ?? "");
  return {
    po_format: t(po.po_format),
    po_revision: t(po.po_revision),
    po_revision_date: t(po.po_revision_date),
    vendor_name: t(po.vendor_name),
    customer_name: t(po.customer_name),
    customer_code: t(po.customer_code),
    payment_terms: t(po.payment_terms),
    freight_terms: t(po.freight_terms),
    fob: t(po.fob),
    shipping_method: t(po.shipping_method),
    // ⚠️ A STORED 0 IS SHOWN AS EMPTY, NOT AS "0". The column is a Float with
    // no null state, so a PO that printed no total reads back 0 -- and "0"
    // in this box would be a total somebody typed.
    po_total: po.po_total ? String(po.po_total) : "",
    po_total_text: t(po.po_total_text),
    contact_emails: t(po.contact_emails),
    contact_phones: t(po.contact_phones),
    extraction_flags: t(po.extraction_flags),
  };
}

/** The extractor's header as the form edits it.
 *
 *  ⚠️ THE WIRE NAMES AND THE COLUMN NAMES DIFFER IN TWO PLACES and the
 *  mapping is explicit rather than a spread: the reader calls them `vendor`
 *  and `po_total_raw`, the record calls them `vendor_name` and
 *  `po_total_text`. A spread would silently drop both, and a vendor warning
 *  that never fires looks exactly like a vendor that is ours. */
function headerFromExtracted(h: ExtractedHeader): PoHeaderDraft {
  const t = (v: string | null | undefined) => (v ?? "");
  return {
    po_format: t(h.format),
    po_revision: t(h.po_revision),
    po_revision_date: t(h.po_revision_date),
    vendor_name: t(h.vendor),
    customer_name: t(h.customer_name),
    customer_code: t(h.customer_code),
    payment_terms: t(h.payment_terms),
    freight_terms: t(h.freight_terms),
    fob: t(h.fob),
    shipping_method: t(h.shipping_method),
    po_total: typeof h.po_total === "number" ? String(h.po_total) : "",
    po_total_text: t(h.po_total_raw),
    // ⚠️ EVERY CONTACT, EACH WITH ITS OWN LABEL, ONE PER LINE. The real
    // document prints two e-mail addresses under two different headings;
    // picking one would present a guess as a fact.
    contact_emails: (h.emails ?? []).map((e) => `${e.label}: ${e.value}`).join("\n"),
    contact_phones: (h.phones ?? []).map((e) => `${e.label}: ${e.value}`).join("\n"),
    extraction_flags: (h.flags ?? []).join("\n"),
  };
}

function headerHasAnything(h: PoHeaderDraft | null): boolean {
  return !!h && Object.values(h).some((v) => v.trim() !== "");
}

/** A stored PO as form rows. Numbers become the strings the inputs edit. */
function rowsFromPo(po: QuotePo): Draft[] {
  if (!po.lines.length) return [{ ...EMPTY_ROW }];
  return po.lines.map((l) => ({
    part_number: l.part_number ?? "",
    quantity: String(l.quantity),
    unit_price: String(l.unit_price),
    manufacturer: l.manufacturer ?? "",
    description: l.description ?? "",
    // ⚠️ CARRIED BACK OUT AS THEY CAME IN. Without these a re-save of a
    // loaded PO would erase its UOM, line number, supplier code and printed
    // price text -- silently, because the form has no box for them.
    ...(l.line_number ? { line_number: l.line_number } : {}),
    ...(l.uom ? { uom: l.uom } : {}),
    ...(l.unit_price_text ? { unit_price_text: l.unit_price_text } : {}),
    ...(l.extended_price != null ? { extended_price: String(l.extended_price) } : {}),
    ...(l.extended_price_text ? { extended_price_text: l.extended_price_text } : {}),
    ...(l.anixter_part_number ? { anixter_part_number: l.anixter_part_number } : {}),
    ...(l.shipments_note ? { shipments_note: l.shipments_note } : {}),
    // Display only -- see the Draft field's note. Absent stays absent.
    ...(l.line_origin ? { line_origin: l.line_origin } : {}),
    // ⚠️ PROVENANCE IS CARRIED BACK OUT. OWNER DECISION, 2026-09-17.
    //
    // A line's origin records WHO FIRST READ THE DIGITS, and editing a line
    // does not change that -- the same rule the review table already applies
    // to an edit made before Confirm. A re-save REPLACES the PO's lines
    // wholesale, so without this the new rows would be created with no claim
    // and the backend would record them "manual": a saved extracted line
    // would silently become a typed one on the next press.
    //
    // ⚠️ IT IS SET FROM THE RECORDED COLUMN, NEVER FROM ANYTHING LOCAL. Only
    // a line the backend already recorded as "extracted" carries the claim
    // back. A hand-typed line has no line_origin to promote it, which is what
    // stops this from becoming a way to label typed rows machine-read.
    ...(l.line_origin === "extracted" ? { from_extraction: true } : {}),
  }));
}

/** The PO's own date, currency and source document.
 *
 *  ⚠️ NOT PART OF PoHeaderDraft, AND THAT IS DELIBERATE. Those twelve fields
 *  are editable strings; these three are a date, a code that must match a
 *  res.currency exactly, and an attachment id. None belongs in a text box. */
type PoMeta = {
  po_date: string | null;
  /** The ISO code as PRINTED. Sent only when it matches a known currency. */
  currency: string | null;
  /** The attachment the rows were read from. */
  document_id: number | null;
};

const EMPTY_PO_META: PoMeta = { po_date: null, currency: null, document_id: null };

/** The header as the save wire carries it.
 *
 *  ⚠️ EXACTLY THE BACKEND'S `_PO_HEADER_FIELDS` AND NOTHING ELSE. The route
 *  ignores unknown keys, which is precisely so a caller cannot hand-write a
 *  field it has no business setting -- so widening this object would be the
 *  only way to break that, and there is no reason to.
 *
 *  ⚠️ AND NOTHING THAT COULD REACH A res.partner. `customer_name` and the
 *  contacts are TEXT the document printed. Saving them creates no customer,
 *  renames none, and adds no contact; `test_po_header.py` tests that in both
 *  directions.
 *
 *  ⚠️ AN EMPTY BOX SENDS "" AND THAT IS DELIBERATE HERE, unlike the line
 *  fields. On the header, a person clearing a box MEANS "the document does
 *  not say this" -- and the backend maps "" to False, i.e. cleared. Omitting
 *  it instead would make a correction impossible: the old value would stay. */
function headerPayload(header: PoHeaderDraft | null): PoHeaderInput {
  if (!header) return {};
  const total = header.po_total.trim();
  return {
    po_format: header.po_format.trim(),
    po_revision: header.po_revision.trim(),
    // ⚠️ A DATE THE BACKEND CANNOT PARSE IS NOT SENT AT ALL. The column is a
    // Date; handing it a half-typed "2026-07" would fail the whole save, and
    // the reader deliberately leaves an ambiguous date EMPTY rather than
    // guessing -- so an unparseable one here is the expected case, not a bug.
    ...(/^\d{4}-\d{2}-\d{2}$/.test(header.po_revision_date.trim())
      ? { po_revision_date: header.po_revision_date.trim() }
      : { po_revision_date: null }),
    vendor_name: header.vendor_name.trim(),
    customer_name: header.customer_name.trim(),
    customer_code: header.customer_code.trim(),
    payment_terms: header.payment_terms.trim(),
    freight_terms: header.freight_terms.trim(),
    fob: header.fob.trim(),
    shipping_method: header.shipping_method.trim(),
    // ⚠️ A BLANK TOTAL IS NOT ZERO. Number("") is 0, and a PO total of 0
    // would read as a real claim about the order's value.
    ...(total && Number.isFinite(Number(total)) ? { po_total: Number(total) } : {}),
    po_total_text: header.po_total_text.trim(),
    contact_emails: header.contact_emails.trim(),
    contact_phones: header.contact_phones.trim(),
    extraction_flags: header.extraction_flags.trim(),
  };
}

const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`,
  fontSize: typography.fontSize.sm, color: semanticColors.textPrimary,
  backgroundColor: semanticColors.surface,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  outline: "none", minWidth: 0,
};

const SECONDARY_BUTTON: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[10]}px`,
  backgroundColor: semanticColors.surface,
  color: semanticColors.textSecondary,
  border: `1px solid ${semanticColors.border}`,
  borderRadius: radius.base,
  fontSize: typography.fontSize.sm, cursor: "pointer",
};

export function PoComparison({
  quoteId, canWrite: mayWriteThisQuote = true, onOutcome, onShipToSet, onPosChanged,
}: {
  quoteId: number;
  // Owner or Admin -- see PoDocuments. Role alone offered Save and Remove to
  // every Sales user on quotes the backend now refuses them.
  canWrite?: boolean;
  // quote-won-lost: the quote's outcome as the PO save reported it (a PO on
  // a finalised quote marks it Won), so the screen can show it at once.
  onOutcome?: (outcome: QuoteOutcome) => void;
  // po-deliver-to-match: "Use this ship-to" was pressed and the backend set
  // it, so the quote screen re-reads the quote.
  onShipToSet?: () => void;
  // A PO was recorded or removed. The Start-from-PO strip decides what to
  // offer ("Add these lines", or deleting the still-empty draft) from the
  // quote's POs, so it has to re-read them.
  onPosChanged?: () => void;
}) {
  const canWrite = useCanEditQuotes() && mayWriteThisQuote;

  const [pos, setPos] = useState<QuotePo[]>([]);
  const [comparison, setComparison] = useState<PoComparisonResult | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // ⚠️ WHICH PO IS ASKING "ARE YOU SURE" (TASK 3). Deleting a recorded PO was
  // one click beside Compare, and a mis-click destroyed typed data with no
  // undo -- which is how the owner lost theirs on 2026-09-10. The trash button
  // now only opens a confirmation that NAMES the PO; the delete request is
  // sent from that confirmation and nowhere else.
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const [reference, setReference] = useState("");
  const [rows, setRows] = useState<Draft[]>([{ ...EMPTY_ROW }]);

  // ⚠️ THE HEADER THE DOCUMENT PRINTED, HELD BESIDE THE LINES AND SAVED WITH
  // THEM. Null means this PO has no header -- a typed one, or one recorded
  // before the format profiles existed -- and the panel is then absent rather
  // than an empty grid of boxes suggesting somebody forgot to fill it in.
  const [header, setHeader] = useState<PoHeaderDraft | null>(null);

  // ⚠️ THREE FIELDS THAT ARE NOT TEXT BOXES, AND WERE THEREFORE DROPPED.
  //
  // The extractor reads the PO's own date and currency, the app knows which
  // PDF it read, and the save route accepts all three -- but the header panel
  // is a grid of editable strings and none of these is one, so `headerPayload`
  // never sent them. Found by reading the FIRST REAL SAVED RECORD on live
  // (PO 3 on quote 568): all three came back empty. No test caught it,
  // because every test asserted the fields that were sent.
  //
  // ⚠️ THE CURRENCY IS THE ONE THAT MATTERED. po_comparison refuses to compare
  // only when BOTH currencies are known and differ, so an unrecorded PO
  // currency means that refusal CAN NEVER FIRE -- a USD PO would come back
  // "Unit price differs" against INR quote prices instead of refusing. A
  // silent, confident, wrong verdict.
  //
  // Held apart from PoHeaderDraft on purpose: that type drives the editable
  // grid, and putting a currency code or an attachment id in it would offer a
  // text box over a value that must either match a record exactly or not be
  // sent at all.
  const [poMeta, setPoMeta] = useState<PoMeta>(EMPTY_PO_META);
  // The same three, as read but NOT YET CONFIRMED -- nothing crosses into the
  // form until Add is pressed, exactly like the header and the rows.
  const [reviewMeta, setReviewMeta] = useState<PoMeta>(EMPTY_PO_META);
  // ⚠️ THE CODES THIS DEPLOYMENT ACTUALLY HAS. Fetched, never hardcoded: the
  // save route resolves a currency by name and RAISES on one it cannot find,
  // so the screen checks before sending. An empty list means "could not
  // check", which is reported differently from "not recognised" -- see
  // currencyRecognition.
  const [currencyCodes, setCurrencyCodes] = useState<string[]>([]);
  // The header being reviewed, editable BEFORE it reaches the form. Separate
  // from `header` for the same reason the review rows are separate from the
  // form rows: nothing crosses into the form until Add is pressed.
  const [reviewHeader, setReviewHeader] = useState<PoHeaderDraft | null>(null);

  // ⚠️ WHAT THE WARNINGS ARE WEIGHED AGAINST, AND ALL THREE COME FROM THE
  // BACKEND. The quote's customer and currency ride the PO list read (one
  // definition each, the same ones the comparison uses); our own company name
  // comes from Settings. Read here rather than passed in as props, so a
  // display string on the quote screen cannot decide whether a warning fires.
  //
  // ⚠️ A FAILED READ LEAVES THEM NULL, AND NULL RAISES NO WARNING. That is
  // the honest direction: an unknown is not a disagreement. It does mean a
  // Settings read failure leaves no company name and no entity names, so a
  // named vendor is reported (an amber bar) rather than silently cleared.
  const [quoteCustomerName, setQuoteCustomerName] = useState<string | null>(null);
  const [quoteCurrency, setQuoteCurrency] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  // Configurable identity (P1-6): our entity names and the label of the
  // supplier's own part-number column, both from Settings > Company.
  const [ourEntityAliases, setOurEntityAliases] = useState<string[]>([]);
  const [supplierCodeLabel, setSupplierCodeLabel] = useState(DEFAULT_SUPPLIER_CODE_LABEL);

  // ⚠️ PASTING A TABLE, AND WHY IT IS A THREE-STATE FLOW RATHER THAN ONE CLICK.
  //
  // "closed" -> "pasting" (a textarea) -> "review" (what was parsed) -> rows.
  // The review state is NOT a convenience and must not be optimised away: it
  // is the whole difference between a person confirming twenty lines and a
  // parser silently deciding them. Nothing reaches the form, let alone the
  // server, until Confirm is pressed -- which is the same posture as the
  // comparison PROPOSING verdicts and a person deciding, and it is the seam
  // extraction will plug into later.
  const [pasteStage, setPasteStage] = useState<"closed" | "pasting" | "review">("closed");
  // ⚠️ ONE REVIEW STAGE, TWO PRODUCERS. Extraction plugs into the SAME
  // confirm-before-save seam the paste path uses -- the review table, its
  // edits and its Confirm are untouched. Only the SOURCE differs, and it
  // differs for exactly one reason: what `from_extraction` is set to.
  const [reviewSource, setReviewSource] = useState<"paste" | "extract">("paste");
  // What extraction reported ALONGSIDE the rows: a partial text layer, the
  // document-total check. Null on the paste path.
  const [extractInfo, setExtractInfo] = useState<ExtractResult | null>(null);
  // A refusal with its own wording and remedy -- NOT an error string.
  const [extractRefusal, setExtractRefusal] = useState<ExtractResult | null>(null);
  const [extracting, setExtracting] = useState(false);
  // po-deliver-to-match: the Deliver-To code the extraction read (stored on
  // the PO when it is saved) and the one-click ship-to's progress.
  const [deliverToCode, setDeliverToCode] = useState<string | null>(null);
  const [shipToBusy, setShipToBusy] = useState(false);
  const [shipToDone, setShipToDone] = useState(false);
  const [shipToError, setShipToError] = useState("");
  const [docs, setDocs] = useState<QuoteDocument[]>([]);
  const [chosenDoc, setChosenDoc] = useState<number | null>(null);
  const [pasteText, setPasteText] = useState("");
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  // Edits made IN REVIEW, keyed by row index. These are what get confirmed --
  // the parse is a proposal, not a result.
  const [reviewEdits, setReviewEdits] = useState<Record<number, Partial<Draft>>>({});
  // Which recorded PO the form is showing, or null when it is a blank form.
  const [loadedPoId, setLoadedPoId] = useState<number | null>(null);

  // ⚠️ AUTO-LOAD HAPPENS ONCE, ON THE FIRST READ, AND NEVER OVER TYPING. The
  // list is re-read after every save; without this flag each refresh would
  // pour the stored PO back over whatever the person was in the middle of
  // entering.
  const autoLoadDone = useRef(false);

  const loadPo = useCallback((po: QuotePo) => {
    setReference(po.po_reference);
    setRows(rowsFromPo(po));
    // ⚠️ AND ITS HEADER. A stored header that did not come back into the form
    // would be shown as blank boxes and then ERASED by the next save, which
    // is the same "loaded but not carried" defect the line fields above had.
    const stored = headerFromPo(po);
    setHeader(headerHasAnything(stored) ? stored : null);
    // ⚠️ AND THE THREE THAT ARE NOT TEXT BOXES, so a re-save resends them
    // instead of silently dropping what is already recorded. The save route
    // only ever SETS these when they are truthy -- it cannot clear them -- so
    // failing to carry them would have left the stored values alone rather
    // than blanking them; but a person who corrects the reference and saves
    // would still expect the rest of the record to come along.
    setPoMeta({
      po_date: po.po_date ?? null,
      currency: po.currency ?? null,
      document_id: po.document_id ?? null,
    });
    setLoadedPoId(po.id);
    setComparison(null);
    setError("");
  }, []);

  const load = useCallback(async () => {
    setStatus("loading");
    setError("");
    const result = await listQuotePos(quoteId);
    if (!result.ok) {
      setPos([]);
      setError(result.error ?? "Could not load the PO data.");
      setStatus("error");
      return;
    }
    const found = result.pos ?? [];
    setPos(found);
    // The quote's own customer and currency, for the warnings. Written on
    // every refresh because they are facts about the quote, not about a PO.
    setQuoteCustomerName(result.quoteCustomerName ?? null);
    setQuoteCurrency(result.quoteCurrency ?? null);
    setStatus("ok");

    // ⚠️ EXACTLY ONE RECORDED PO IS LOADED. MORE THAN ONE IS NOT.
    //
    // One is UNAMBIGUOUS: it is the only PO this quote has, so there is
    // nothing to choose between and showing it is not a guess. More than one
    // is a CHOICE, and the panel does not make it -- it lists them and asks,
    // the same posture as the `ambiguous` verdict. Opening the most recent
    // one would be a plausible-looking pick that is wrong exactly when it
    // matters: somebody revising the older PO would be shown the newer, edit
    // it, and save over it.
    if (!autoLoadDone.current) {
      autoLoadDone.current = true;
      if (found.length === 1) loadPo(found[0]);
    }
  }, [quoteId, loadPo]);

  useEffect(() => { void load(); }, [load]);

  // ⚠️ THE ACTION ONLY EXISTS WHERE A PDF IS ATTACHED AND THE USER MAY WRITE.
  // Not greyed out, not present-and-refusing: absent. There is nothing to
  // extract from, and a control that cannot work is a promise the screen
  // cannot keep -- the same call ReadOnlySave's header makes about disabled
  // buttons. Non-PDF attachments are filtered out here rather than refused
  // later, because the backend can only read a PDF and saying so after the
  // click would be a worse way to learn it.
  // Our own company name, for the vendor check. One read, on mount.
  //
  // ⚠️ IT IS THE SETTINGS NAME, NOT A CONSTANT IN THE BUNDLE. An owner who
  // renames the company in Settings would otherwise keep seeing a vendor
  // warning on every one of their own POs, with nothing on screen to explain
  // why. A failed read leaves it null, which turns the NAME half of the check
  // off rather than firing it against a guess.
  // The currency codes this deployment has adopted. One read, on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await listCurrencies();
      if (cancelled) return;
      setCurrencyCodes(list.map((c) => c.code).filter(Boolean));
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await api.getCompanySettingsFromBackend();
      if (cancelled) return;
      if (result.ok && result.settings) {
        setCompanyName(result.settings.company_name ?? null);
        setOurEntityAliases(entityAliasList(result.settings.our_entity_aliases));
        if (result.settings.supplier_code_label) setSupplierCodeLabel(result.settings.supplier_code_label);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!canWrite) { setDocs([]); return; }
    let cancelled = false;
    (async () => {
      const result = await listQuoteDocuments(quoteId);
      if (cancelled || !result.ok) return;
      setDocs((result.documents ?? []).filter((d) => d.mimetype === "application/pdf"));
    })();
    return () => { cancelled = true; };
  }, [quoteId, canWrite]);

  // ⚠️ A CELL THE PARSER COULD NOT READ BECOMES AN EMPTY STRING IN THE FORM,
  // NEVER A ZERO. The existing typed path already refuses to save a line whose
  // quantity or unit price is not a finite number, so an unreadable cell lands
  // in the form as something the person must fill in -- which is exactly the
  // behaviour wanted. Writing 0 here would sail through that check and compare
  // as a real claim about what the customer ordered.
  // ⚠️ A ROW FROM EITHER PRODUCER, AND THE EXTRACTOR'S IS WIDER. The paste
  // parser emits five cells; a format profile emits those plus a line number,
  // a UOM, an extended price, the supplier's own code and the shipments. The
  // review table renders ONE shape, so the wider fields are optional here --
  // a pasted row simply has none of them and every `...(x ? {} : {})` below
  // omits its key.
  function draftFromParsed(row: ParsedRow & Partial<ExtractedRow>): Draft {
    const edit = reviewEdits[row.index] ?? {};
    const cell = (c: { kind: string; text?: string }) =>
      c.kind === "value" ? (c.text ?? "") : "";
    // ⚠️ A NUMERIC CELL CONTRIBUTES ITS PARSED NUMBER, NOT THE DOCUMENT'S
    // TYPOGRAPHY -- and this was a real, silent defect on the PASTE path
    // before extraction existed.
    //
    // Both producers keep the ORIGINAL text on a number cell ("Rs 7,128.00",
    // "1,500.00") alongside the parsed `n`. Putting that text into the form
    // meant onSave ran Number("1,500.00"), got NaN, and REFUSED the line with
    // "Every line needs a numeric quantity and unit price" -- on a figure it
    // had read perfectly well. The paste suite never caught it because every
    // fixture used plain unpunctuated numbers.
    //
    // So the form takes `n`. It also makes the review table honest: what is
    // shown is exactly what will be saved, rather than a prettier string that
    // is about to be rejected.
    const numeric = (c: { kind: string; text?: string; n?: number }) =>
      c.kind === "value" && typeof c.n === "number" ? String(c.n) : cell(c);
    // ⚠️ RESOLVED ONCE, ABOVE THE OBJECT. Each of these is "what the person
    // typed, or else what the document printed", and inlining that choice
    // into a spread made it easy to get the precedence backwards on one field
    // and not notice -- the value would simply stop being editable.
    // ⚠️ EACH OF THESE CELLS MAY BE ABSENT ENTIRELY, not merely unreadable,
    // and the two are different. A PASTED row has no uom, lineTotal or
    // supplier-code cell AT ALL -- `cell(undefined)` threw on `c.kind` and took the
    // whole review table down with it, which the paste suite caught. An
    // absent cell resolves to "", and "" omits its key from the payload.
    const lineNumber = edit.line_number ?? (row.lineNumber ?? "");
    const uom = edit.uom ?? (row.uom ? cell(row.uom) : "");
    // ⚠️ "AS PRINTED" IS A CLAIM ABOUT A DOCUMENT, SO ONLY THE EXTRACT PATH
    // MAY MAKE IT. A pasted row's `text` is what somebody had on their
    // clipboard -- real enough, but not "what the page printed", and storing
    // it in unit_price_text would put a typographic claim on the record that
    // no document backs. It also broke a standing invariant the paste slice
    // established and tests: the save payload is IDENTICAL whether a line was
    // typed or pasted. Gated the same way from_extraction is.
    const fromDocument = reviewSource === "extract";
    const unitPriceText = fromDocument && row.unitPrice.kind === "value"
      ? (row.unitPrice.text ?? "") : "";
    const extendedPrice = edit.extended_price
      ?? (row.lineTotal ? numeric(row.lineTotal) : "");
    const extendedPriceText =
      fromDocument && row.lineTotal && row.lineTotal.kind === "value"
        ? (row.lineTotal.text ?? "") : "";
    const anixterPart = edit.anixter_part_number
      ?? (row.anixterPartNumber ? cell(row.anixterPartNumber) : "");
    const note = shipmentsNote(row.shipments);
    return {
      part_number: edit.part_number ?? cell(row.partNumber),
      quantity: edit.quantity ?? numeric(row.quantity),
      unit_price: edit.unit_price ?? numeric(row.unitPrice),
      manufacturer: edit.manufacturer ?? cell(row.manufacturer),
      description: edit.description ?? cell(row.description),
      // ⚠️ SET FROM THE SOURCE OF THE REVIEW, not from anything the row says.
      // A pasted row is typed by a person; an extracted one was read by a
      // machine and then confirmed. Editing the cell does not change who
      // originated it.
      ...(reviewSource === "extract" ? { from_extraction: true } : {}),
      // --- CARRIED THROUGH TO THE SAVE, from the row as read.
      //
      // ⚠️ NOT DERIVED HERE. These are what the document printed. The review
      // table shows them and a person can correct them, and an edit lands in
      // `reviewEdits` exactly like the five fields above.
      //
      // ⚠️ AN EMPTY VALUE OMITS THE KEY RATHER THAN SENDING "". The backend
      // treats an absent key as "leave it alone" and "" as "clear it", so a
      // field the document did not print must not be sent at all.
      ...(lineNumber ? { line_number: lineNumber } : {}),
      ...(uom ? { uom } : {}),
      // ⚠️ THE PRICE AS PRINTED, BESIDE THE NUMBER -- NOT INSTEAD OF IT. The
      // form's own unit_price above takes the PARSED number, because
      // Number("10,322.0000") is NaN and the save check would refuse a figure
      // that was read perfectly well. This carries the typography, so the
      // screen and the record can both show every decimal the page printed.
      ...(unitPriceText ? { unit_price_text: unitPriceText } : {}),
      // ⚠️ THE EXTENDED PRICE IS THE DOCUMENT'S OR IT IS ABSENT. Never
      // quantity x unit price -- that figure appears on no page of the PO.
      ...(extendedPrice ? { extended_price: extendedPrice } : {}),
      ...(extendedPriceText ? { extended_price_text: extendedPriceText } : {}),
      ...(anixterPart ? { anixter_part_number: anixterPart } : {}),
      // ⚠️ SEVERAL SHIPMENTS MEANS NO PRINTED QUANTITY, AND THE NOTE RECORDS
      // WHY. The quantity cell is left for a person; it is never the sum.
      ...(note ? { shipments_note: note } : {}),
    };
  }

  function onParse() {
    const result = parsePastedPoLines(pasteText);
    setReviewSource("paste");
    // ⚠️ A PASTE HAS NO HEADER, and null rather than an empty draft is the
    // difference between "this path reads no header" and "the document
    // printed nothing" -- the second would show twelve blank boxes.
    setReviewHeader(null);
    setReviewMeta(EMPTY_PO_META);
    setExtractInfo(null);
    setParsed(result);
    setReviewEdits({});
    setPasteStage("review");
    setError("");
  }

  /** Read an attached PDF and hand the rows to the SAME review stage.
   *
   *  ⚠️ A REFUSAL IS A STATE, NOT AN ERROR. A scanned PDF, a missing header
   *  and a non-PDF are things this screen EXPLAINS with a remedy; only a dead
   *  session or a broken backend goes to the red error bar. */
  async function onExtract(documentId: number) {
    setExtracting(true);
    setExtractRefusal(null);
    setError("");
    const result = await extractPoDocument(quoteId, documentId);
    setExtracting(false);

    if (!result.ok) {
      if (result.reason) {
        // A structured refusal: no text layer, no header, not a PDF, no
        // extractor. Each has its own wording below.
        setExtractRefusal(result);
        return;
      }
      setError(result.error ?? "Could not read that document.");
      return;
    }

    setReviewSource("extract");
    setExtractInfo(result);
    setDeliverToCode(result.header?.deliver_to_code ?? null);
    setShipToDone(false);
    setShipToError("");
    // The header the profile read, editable in the review before it reaches
    // the form. Absent on the generic path, which produces no header at all.
    setReviewHeader(result.header ? headerFromExtracted(result.header) : null);
    // ⚠️ AND WHICH DOCUMENT IT WAS READ FROM. The app knows it here and
    // nowhere else afterwards, so not capturing it is how the PO ended up
    // unlinked from the PDF it came out of.
    setReviewMeta({
      po_date: result.header?.po_date ?? null,
      currency: result.header?.currency ?? null,
      document_id: documentId,
    });
    setParsed({
      // The extractor's cells are already poPaste's Cell shape, which is why
      // the review table needs no change at all.
      rows: (result.rows ?? []) as unknown as ParsedRow[],
      delimiter: "none",
      skippedHeader: null,
      ambiguous: false,
      ambiguityReason: "",
    });
    setReviewEdits({});
    setPasteStage("review");
  }

  function onCancelPaste() {
    setPasteStage("closed");
    setPasteText("");
    setParsed(null);
    setReviewHeader(null);
    setReviewMeta(EMPTY_PO_META);
    setReviewEdits({});
    setExtractInfo(null);
    setExtractRefusal(null);
    setReviewSource("paste");
    setDeliverToCode(null);
  }

  // ⚠️ CONFIRM APPENDS TO THE EXISTING ROWS -- IT DOES NOT REPLACE THEM. The
  // brief is explicit that pasting ADDS to the typed path; a paste that wiped
  // hand-typed lines would be the "usable once" shape this project keeps
  // catching. The trailing blank row the form always carries is dropped first
  // so a paste does not leave a gap in the middle.
  function onConfirmPaste() {
    if (!parsed) return;
    const appended = parsed.rows.map(draftFromParsed);
    const existing = rows.filter(
      (r) => r.part_number.trim() !== "" || r.quantity.trim() !== "" || r.unit_price.trim() !== "",
    );
    setRows([...existing, ...appended]);
    // ⚠️ THE HEADER IS CONFIRMED WITH THE LINES, ON THE SAME PRESS. That is
    // what the brief asks for and it is also the honest shape: the header and
    // the lines were read off one document, so accepting half of it would
    // record a PO whose details came from somewhere else.
    if (reviewHeader) setHeader(reviewHeader);
    // Confirmed on the SAME press as the header and the lines.
    if (reviewMeta.po_date || reviewMeta.currency || reviewMeta.document_id) {
      setPoMeta(reviewMeta);
    }
    // ⚠️ AND THE PO NUMBER FILLS THE REFERENCE ONLY WHEN IT IS STILL BLANK.
    // Overwriting a reference somebody typed -- or one a loaded PO is keyed
    // on -- would silently retarget the save at a different record, because
    // the backend matches on (quote, reference).
    const poNumber = extractInfo?.header?.po_number;
    if (poNumber && reference.trim() === "") setReference(poNumber);
    // po-deliver-to-match: the code outlives the review -- it rides the save.
    const keepDeliverTo = deliverToCode;
    onCancelPaste();
    setDeliverToCode(keepDeliverTo);
  }

  function onNewPo() {
    setReference("");
    setRows([{ ...EMPTY_ROW }]);
    setHeader(null);
    setPoMeta(EMPTY_PO_META);
    setLoadedPoId(null);
    setComparison(null);
    setError("");
  }

  async function onSave() {
    // ⚠️ A BLANK FIGURE IS MISSING, NOT ZERO, AND Number("") IS 0.
    //
    // This validation read `!Number.isFinite(Number(r.quantity))`, and
    // Number("") is 0 -- a finite number -- so a line with an EMPTY quantity
    // sailed through the check below and was saved as a quantity of ZERO. On a
    // PO that is a claim about what the customer ordered, and the comparison
    // would report it as a real disagreement with the quote.
    //
    // It was always reachable by leaving the field blank; the paste path made
    // it reachable twenty rows at a time, because an UNREADABLE cell lands in
    // the form empty by design. Caught by the paste suite asserting that such
    // a line is REFUSED.
    const asNumber = (raw: string) => (raw.trim() === "" ? NaN : Number(raw));
    const lines = rows
      .filter((r) => r.part_number.trim() !== "")
      .map((r) => ({
        part_number: r.part_number.trim(),
        quantity: asNumber(r.quantity),
        unit_price: asNumber(r.unit_price),
        ...(r.manufacturer.trim() ? { manufacturer: r.manufacturer.trim() } : {}),
        ...(r.description.trim() ? { description: r.description.trim() } : {}),
        // ⚠️ THIS IS WHAT MAKES line_origin TRUE. The backend refuses
        // "extracted" unless the row says so here, so if this key is ever
        // dropped between the review table and this payload, every extracted
        // row saves as "manual" -- silently, with no error and no visible
        // difference. Sent only when true: absent means manual, which is the
        // backend's default and the honest one.
        ...(r.from_extraction ? { from_extraction: true } : {}),
        // --- carried through. An empty value omits the key, because the
        // backend reads an absent key as "leave it alone".
        ...(r.line_number?.trim() ? { line_number: r.line_number.trim() } : {}),
        ...(r.uom?.trim() ? { uom: r.uom.trim() } : {}),
        ...(r.unit_price_text?.trim() ? { unit_price_text: r.unit_price_text.trim() } : {}),
        // ⚠️ SENT ONLY WHEN THE DOCUMENT PRINTED ONE, AND NEVER COMPUTED.
        // `asNumber` is reused so a blank stays NaN rather than becoming 0 --
        // but an extended price is OPTIONAL, so a blank omits the key
        // entirely instead of failing the line the way a blank quantity does.
        ...(r.extended_price?.trim() && Number.isFinite(asNumber(r.extended_price))
          ? { extended_price: asNumber(r.extended_price) } : {}),
        ...(r.extended_price_text?.trim()
          ? { extended_price_text: r.extended_price_text.trim() } : {}),
        ...(r.anixter_part_number?.trim()
          ? { anixter_part_number: r.anixter_part_number.trim() } : {}),
        ...(r.shipments_note?.trim() ? { shipments_note: r.shipments_note.trim() } : {}),
      }));

    if (!reference.trim()) { setError("Enter the customer's PO reference."); return; }
    if (lines.length === 0) { setError("Enter at least one PO line."); return; }
    if (lines.some((l) => !Number.isFinite(l.quantity) || !Number.isFinite(l.unit_price))) {
      setError("Every line needs a numeric quantity and unit price.");
      return;
    }

    setBusy(true);
    setError("");
    // No po_id is sent. The backend matches on (quote, reference): the same
    // reference REVISES the stored PO and replaces its lines; a different
    // reference records a new one.
    // ⚠️ THE THREE FIELDS THE FORM USED TO DROP.
    //
    // Each is sent only when it has a value: the route SETS them when truthy
    // and never clears them, so sending an empty one would be a no-op at best
    // and a silent wipe attempt at worst.
    //
    // ⚠️ THE CURRENCY IS SENT ONLY WHEN IT MATCHES A KNOWN CODE EXACTLY. The
    // route resolves by name and RAISES on a code it cannot find, so sending
    // an unrecognised one would fail the whole save -- the person would lose
    // every other field over a currency. Unrecognised is reported in amber
    // and left off, which `currencyRecognition` decides.
    const currencyToSend = currencyRecognition(poMeta.currency, currencyCodes).send;
    const saved = await saveQuotePo(quoteId, {
      po_reference: reference.trim(), lines, ...headerPayload(header),
      ...(poMeta.po_date ? { po_date: poMeta.po_date } : {}),
      ...(currencyToSend ? { currency: currencyToSend } : {}),
      ...(poMeta.document_id ? { document_id: poMeta.document_id } : {}),
      // po-deliver-to-match: kept on the PO for reference; sets nothing.
      ...(deliverToCode ? { deliver_to_code: deliverToCode } : {}),
    });
    if (!saved.ok || !saved.po) {
      setBusy(false);
      setError(saved.error ?? "The PO could not be saved.");
      return;
    }
    setLoadedPoId(saved.po.id);
    if (saved.outcome && onOutcome) onOutcome(saved.outcome);
    onPosChanged?.();
    const compared = await compareQuotePo(quoteId, saved.po.id);
    setBusy(false);
    if (!compared.ok || !compared.comparison) {
      setError(compared.error ?? "The comparison could not be run.");
      return;
    }
    setComparison(compared.comparison);
    void load();
  }

  async function onCompareExisting(po: QuotePo) {
    setBusy(true);
    setError("");
    const compared = await compareQuotePo(quoteId, po.id);
    setBusy(false);
    if (!compared.ok || !compared.comparison) {
      setError(compared.error ?? "The comparison could not be run.");
      return;
    }
    setComparison(compared.comparison);
  }

  async function onDelete(po: QuotePo) {
    setConfirmDeleteId(null);
    setBusy(true);
    setError("");
    const result = await deleteQuotePo(quoteId, po.id);
    setBusy(false);
    if (!result.ok) { setError(result.error ?? "The PO could not be removed."); return; }
    if (comparison?.po_id === po.id) setComparison(null);
    if (loadedPoId === po.id) onNewPo();
    onPosChanged?.();
    void load();
  }

  const loadedPo = pos.find((p) => p.id === loadedPoId) ?? null;

  // ⚠️ COMPUTED FROM WHATEVER HEADER IS ON SCREEN, so correcting a misread
  // customer name clears its warning immediately -- which is the point of
  // making the fields editable. The decisions are in services/poWarnings.ts;
  // this is only the wiring.
  const quoteFacts = {
    customerName: quoteCustomerName,
    currency: quoteCurrency,
    companyName,
    ourEntityAliases,
  };
  const warningsFor = (h: PoHeaderDraft | null): PoWarning[] =>
    poHeaderWarnings(h ? {
      customer_name: h.customer_name,
      vendor_name: h.vendor_name,
      // ⚠️ THE PO'S CURRENCY IS NOT A HEADER BOX. The reader takes it off the
      // total ("30,966.00 INR"), and it is stored on the PO's own currency
      // link rather than as text -- so the warning reads it from the
      // extraction, or from the loaded record, and not from an input.
      currency: extractInfo?.header?.currency ?? loadedPo?.currency ?? null,
      po_revision: h.po_revision,
    } : null, quoteFacts);
  // ⚠️ THE CURRENCY-RECOGNITION WARNING IS NOT A HEADER-FIELD WARNING, so it
  // is concatenated rather than folded into poHeaderWarnings: that function is
  // pure over what the document says and what the quote says, and this one
  // needs the list of currencies the BACKEND has. Keeping the dependency out
  // of it is what keeps its 42 tests free of I/O.
  const currencyWarn = (meta: PoMeta): PoWarning[] => {
    const w = currencyRecognition(meta.currency, currencyCodes).warning;
    return w ? [w] : [];
  };
  const formWarnings = [...warningsFor(header), ...currencyWarn(poMeta)];
  const reviewWarnings = [...warningsFor(reviewHeader), ...currencyWarn(reviewMeta)];

  return (
    <div
      data-testid="po-comparison"
      style={{
        border: `1px solid ${semanticColors.border}`,
        borderRadius: radius.base,
        backgroundColor: semanticColors.panelBg,
        padding: spacing[12],
        marginBottom: spacing[12],
      }}
    >
      <div className="flex items-center" style={{ gap: spacing[8], marginBottom: spacing[8] }}>
        <ClipboardList size={iconSize.md} strokeWidth={iconStroke.regular} color={colors.indigo600} />
        <span style={{
          fontSize: typography.fontSize.base,
          fontWeight: typography.fontWeight.medium,
          color: semanticColors.textPrimary,
        }}>
          PO check
        </span>
      </div>

      {/* ⚠️ THE HONEST SENTENCE. The comparison runs on the SAVED lines, which
          a person typed, pasted or confirmed one by one after "Extract from
          PDF" -- never on the PDF itself. The note says both halves: reading is
          available and local, and nothing is used unconfirmed. */}
      <div
        data-testid="po-comparison-note"
        style={{
          fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
          marginBottom: spacing[8],
        }}
      >
        Compares the PO lines below against this quote. Type or paste them, or
        use “Extract from PDF”: the document is read on our own server, and
        every row is confirmed by a person before it is saved.
      </div>

      {error && (
        <div
          data-testid="po-comparison-error"
          style={{
            fontSize: typography.fontSize.sm, color: colors.red600,
            backgroundColor: colors.red50, border: `1px solid ${colors.red100}`,
            borderRadius: radius.base, padding: spacing[8], marginBottom: spacing[8],
          }}
        >
          {error}
        </div>
      )}

      {/* ⚠️ MORE THAN ONE RECORDED PO: THE PANEL ASKS. It does not open one on
          the person's behalf -- see the load() comment. */}
      {status === "ok" && pos.length > 1 && loadedPoId === null && (
        <div
          data-testid="po-multiple-choose"
          style={{
            fontSize: typography.fontSize.sm, color: semanticColors.accentTextOnTint,
            backgroundColor: colors.indigo50, border: `1px solid ${colors.indigo100}`,
            borderRadius: radius.base, padding: spacing[8], marginBottom: spacing[8],
          }}
        >
          This quote has {pos.length} recorded POs. Choose which one to open
          {canWrite ? ", or start a new one below." : "."}
        </div>
      )}

      {/* Which PO the form is showing, so an edit is never ambiguous about
          what it will revise. */}
      {loadedPo && (
        <div
          data-testid="po-loaded-note"
          className="flex items-center"
          style={{
            gap: spacing[8], fontSize: typography.fontSize.sm,
            color: semanticColors.textSecondary, marginBottom: spacing[8],
          }}
        >
          <span style={{ flex: 1 }}>
            {canWrite
              ? <>Showing recorded PO <strong>{loadedPo.po_reference}</strong>. Change it and press Save PO and compare to revise it.</>
              : <>Recorded PO <strong>{loadedPo.po_reference}</strong> (read only).</>}
          </span>
          {canWrite && (
            <button data-testid="po-new" onClick={onNewPo} disabled={busy} style={SECONDARY_BUTTON}>
              New PO
            </button>
          )}
        </div>
      )}

      {canWrite && (
        <div style={{ marginBottom: spacing[12] }}>
          {/* ⚠️ ABOVE THE LINE TABLE, AND EDITABLE, AND SAVED WITH THE LINES.
              Rendered only when this PO HAS a header: a typed PO gets no
              empty grid of boxes implying somebody forgot to fill it in. */}
          {header && (
            <PoHeaderFields
              header={header}
              warnings={formWarnings}
              testIdPrefix="po-header"
              poDate={poMeta.po_date}
              currency={poMeta.currency}
              onChange={(key, value) =>
                setHeader((cur) => (cur ? { ...cur, [key]: value } : cur))}
            />
          )}
          <input
            data-testid="po-reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Customer PO reference, e.g. IN990000001"
            style={{ ...INPUT, width: "100%", marginBottom: spacing[8] }}
          />
          {rows.map((row, i) => (
            <div key={i} className="flex items-center"
                 style={{ gap: spacing[6], marginBottom: spacing[6] }}>
              <input
                data-testid={`po-line-part-${i}`}
                value={row.part_number}
                onChange={(e) => setRows(rows.map((r, j) => j === i ? { ...r, part_number: e.target.value } : r))}
                placeholder="Part number"
                style={{ ...INPUT, flex: 2 }}
              />
              <input
                data-testid={`po-line-qty-${i}`}
                value={row.quantity}
                onChange={(e) => setRows(rows.map((r, j) => j === i ? { ...r, quantity: e.target.value } : r))}
                placeholder="Qty"
                inputMode="decimal"
                style={{ ...INPUT, flex: 1 }}
              />
              {/* ⚠️ LABELLED "Unit price". The comparison compares UNIT to
                  UNIT, and a person copying a PO's "Line Total" into a field
                  called "Price" is the arithmetic mistake this screen exists
                  to surface rather than commit. */}
              <input
                data-testid={`po-line-price-${i}`}
                value={row.unit_price}
                onChange={(e) => setRows(rows.map((r, j) => j === i ? { ...r, unit_price: e.target.value } : r))}
                placeholder="Unit price"
                inputMode="decimal"
                style={{ ...INPUT, flex: 1 }}
              />
              {/* ⚠️ BESIDE THE INPUTS, NOT INSTEAD OF ANY OF THEM. The row is
                  as editable as it was; this only says where its digits came
                  from. Absent on a row somebody typed and on a row whose
                  origin the backend did not report. */}
              <LineOriginTag origin={row.line_origin}
                             testId={`po-line-origin-${i}`} />
              {rows.length > 1 && (
                <button
                  data-testid={`po-line-remove-${i}`}
                  aria-label={`Remove PO line ${i + 1}`}
                  onClick={() => setRows(rows.filter((_, j) => j !== i))}
                  style={{
                    width: iconSize.lg, height: iconSize.lg, padding: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: "transparent", border: "none",
                    color: semanticColors.textPlaceholder, cursor: "pointer",
                  }}
                >
                  <Trash2 size={iconSize.sm} strokeWidth={iconStroke.regular} />
                </button>
              )}
            </div>
          ))}
          {/* ===== EXTRACT: a REFUSAL, stated as a state with a remedy =====
              ⚠️ NOT AN ERROR BAR. A scanned PDF is not a fault -- it is a
              fact about the document, and the only useful response names
              what to do instead. Rendering these red beside "Backend
              returned HTTP 500" would teach the reader to skim both. */}
          {extractRefusal && (
            <div
              data-testid="po-extract-refusal"
              data-reason={extractRefusal.reason}
              style={{
                marginBottom: spacing[12], padding: spacing[12],
                backgroundColor: colors.amber50,
                border: `1px solid ${colors.amber200}`,
                borderRadius: radius.base,
              }}
            >
              <div style={{
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.medium,
                color: semanticColors.textPrimary, marginBottom: spacing[4],
              }}>
                {extractRefusal.reason === "no_text_layer"
                  ? "This PDF cannot be read"
                  : extractRefusal.reason === "no_header"
                    ? "No table header was found"
                    : extractRefusal.reason === "not_a_pdf"
                      ? "That attachment is not a PDF"
                      // ⚠️ ITS OWN HEADING. The deployed bundle fell through
                      // to "The PDF reader is unavailable" for this one,
                      // which is a different and wrong claim -- the reader
                      // ran, read the layout, and refused it. The MESSAGE
                      // underneath was right either way, which is why the
                      // backend was safe to ship first.
                      : extractRefusal.reason === "row_absorbed_page_furniture"
                        ? "This layout could not be read safely"
                        // ⚠️ AND THE LAST BRANCH NOW SAYS SO RATHER THAN
                        // NAMING A CAUSE IT DOES NOT KNOW. A reason this
                        // build has never heard of is exactly the case where
                        // guessing the heading is wrong.
                        : extractRefusal.reason === "extractor_unavailable"
                          ? "The PDF reader is unavailable"
                          : "This PDF could not be read"}
              </div>
              <div style={{
                fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
              }}>
                {extractRefusal.message || extractRefusal.error}
              </div>
              <button
                data-testid="po-extract-refusal-dismiss"
                onClick={() => setExtractRefusal(null)}
                style={{ ...SECONDARY_BUTTON, marginTop: spacing[8] }}
              >
                Close
              </button>
            </div>
          )}

          {/* ===== PASTE: the textarea ===================================== */}
          {pasteStage === "pasting" && (
            <div
              data-testid="po-paste-panel"
              style={{
                marginBottom: spacing[12], padding: spacing[12],
                backgroundColor: semanticColors.panelBg,
                border: `1px solid ${semanticColors.border}`,
                borderRadius: radius.base,
              }}
            >
              <div style={{
                fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
                marginBottom: spacing[8],
              }}>
                Copy the line rows out of the PO and paste them here. Tabs,
                commas or aligned spacing all work.{" "}
                {/* ⚠️ SAID OUT LOUD, because nothing downstream can tell the
                    difference: a space-aligned copy loses empty cells, so a
                    blank quantity may simply not survive the clipboard. */}
                <strong>Nothing is saved until you check what was read.</strong>
              </div>
              <textarea
                data-testid="po-paste-input"
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                rows={6}
                style={{
                  width: "100%", padding: spacing[8],
                  fontFamily: "monospace", fontSize: typography.fontSize.sm,
                  border: `1px solid ${semanticColors.border}`,
                  borderRadius: radius.base, boxSizing: "border-box",
                }}
              />
              <div className="flex items-center" style={{ gap: spacing[8], marginTop: spacing[8] }}>
                <button
                  data-testid="po-paste-parse"
                  className="btn-primary"
                  disabled={pasteText.trim() === ""}
                  onClick={onParse}
                  style={{
                    height: heights.control, padding: `0 ${spacing[14]}px`,
                    backgroundColor: pasteText.trim() === "" ? colors.gray200 : colors.indigo600,
                    color: colors.white, border: "none", borderRadius: radius.base,
                    fontSize: typography.fontSize.base,
                    cursor: pasteText.trim() === "" ? "not-allowed" : "pointer",
                  }}
                >
                  Read the lines
                </button>
                <button data-testid="po-paste-cancel" onClick={onCancelPaste} style={SECONDARY_BUTTON}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* ===== PASTE: THE REVIEW STEP ================================== */}
          {/* ⚠️ MANDATORY. Confirm is the only route from a paste into the
              form, and it is the only thing on this panel that changes any
              state. Nothing here writes to the server. */}
          {pasteStage === "review" && parsed && (
            <div
              data-testid="po-paste-review"
              style={{
                marginBottom: spacing[12], padding: spacing[12],
                backgroundColor: semanticColors.panelBg,
                border: `1px solid ${semanticColors.border}`,
                borderRadius: radius.base,
              }}
            >
              <div style={{
                fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                color: semanticColors.textPrimary, marginBottom: spacing[4],
              }}>
                Check what was read
              </div>
              <div
                data-testid="po-paste-summary"
                style={{
                  fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
                  marginBottom: spacing[8],
                }}
              >
                {parsed.rows.length} row{parsed.rows.length === 1 ? "" : "s"} read
                {parsed.skippedHeader ? ", a header row skipped" : ""}. Edit
                anything that is wrong &mdash; what you see here is what will be
                added.
              </div>

              {/* ⚠️ A PARTIAL TEXT LAYER NAMES THE PAGES. Without this the
                  rows below read as "the PO has this many lines", when what
                  actually happened is that some pages were photographs and
                  their lines were never seen. */}
              {extractInfo?.textLayer?.kind === "partial" && (
                <div
                  data-testid="po-extract-partial"
                  style={{
                    marginBottom: spacing[8], padding: `${spacing[8]}px ${spacing[10]}px`,
                    backgroundColor: colors.amber50,
                    border: `1px solid ${colors.amber200}`,
                    borderRadius: radius.base,
                    fontSize: typography.fontSize.sm, color: semanticColors.textPrimary,
                  }}
                >
                  {extractInfo.textLayer.message}
                </div>
              )}

              {/* ⚠️ THE DOCUMENT-LEVEL CHECK, SEPARATE FROM THE ROWS. It never
                  adjusts a line -- one wrong total must not rewrite figures
                  that were read correctly. */}
              {extractInfo?.documentCheck && !extractInfo.documentCheck.balanced
                && extractInfo.documentCheck.reason && (
                <div
                  data-testid="po-extract-doccheck"
                  style={{
                    marginBottom: spacing[8], padding: `${spacing[8]}px ${spacing[10]}px`,
                    backgroundColor: colors.amber50,
                    border: `1px solid ${colors.amber200}`,
                    borderRadius: radius.base,
                    fontSize: typography.fontSize.sm, color: semanticColors.textPrimary,
                  }}
                >
                  {extractInfo.documentCheck.reason}
                </div>
              )}

              {/* po-deliver-to-match: an EXACT Deliver-To code match under
                  this quote's customer. A suggestion -- nothing is set until
                  "Use this ship-to" is pressed. No match shows nothing. */}
              {extractInfo?.ship_to_suggestion && (
                <div
                  data-testid="po-shipto-suggestion"
                  style={{
                    marginBottom: spacing[8], padding: `${spacing[8]}px ${spacing[10]}px`,
                    backgroundColor: semanticColors.panelBg, border: `1px solid ${semanticColors.border}`,
                    borderRadius: radius.base, fontSize: typography.fontSize.sm,
                    color: semanticColors.textPrimary, display: "flex", alignItems: "center",
                    gap: spacing[8], flexWrap: "wrap",
                  }}
                >
                  <span data-testid="po-shipto-suggestion-text">
                    Ship-to suggested: {extractInfo.ship_to_suggestion.name} ({extractInfo.ship_to_suggestion.code})
                  </span>
                  {(extractInfo.ship_to_suggestion.already_set || shipToDone) ? (
                    <span data-testid="po-shipto-in-use" style={{ color: semanticColors.textSecondary }}>
                      This is the quote's ship-to.
                    </span>
                  ) : canWrite && (
                    <button
                      data-testid="po-shipto-use"
                      disabled={shipToBusy}
                      onClick={async () => {
                        const s = extractInfo.ship_to_suggestion;
                        if (!s) return;
                        setShipToBusy(true);
                        setShipToError("");
                        const r = await setQuoteShipTo(quoteId, s.id);
                        setShipToBusy(false);
                        if (!r.ok) { setShipToError(r.error ?? "The ship-to was not set."); return; }
                        setShipToDone(true);
                        onShipToSet?.();
                      }}
                      style={{
                        height: heights.control, padding: `0 ${spacing[12]}px`,
                        backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
                        border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                        fontSize: typography.fontSize.base, cursor: "pointer",
                      }}
                    >
                      Use this ship-to
                    </button>
                  )}
                  {shipToError && (
                    <span data-testid="po-shipto-error" role="alert" style={{ color: semanticColors.destructive }}>
                      {shipToError}
                    </span>
                  )}
                </div>
              )}

              {/* ⚠️ THE REFUSAL, STATED AS A QUESTION RATHER THAN AN ERROR. The
                  figures could not be told apart, which is the system's
                  problem to own, not the reader's mistake. Same wording
                  posture as the `ambiguous` verdict. */}
              {parsed.ambiguous && (
                <div
                  data-testid="po-paste-ambiguous"
                  style={{
                    marginBottom: spacing[8], padding: `${spacing[8]}px ${spacing[10]}px`,
                    backgroundColor: colors.indigo50,
                    border: `1px solid ${colors.indigo100}`,
                    borderRadius: radius.base,
                    fontSize: typography.fontSize.sm, color: semanticColors.accentTextOnTint,
                  }}
                >
                  We could not tell which column is the unit price, so none was
                  filled in: {parsed.ambiguityReason}. Fill the quantity and
                  unit price in below, or cancel and paste those two columns on
                  their own.
                </div>
              )}

              {/* ⚠️ THE HEADER, ABOVE THE LINE TABLE, BEFORE ANYTHING IS
                  ADDED. Editable here for the same reason the rows are: the
                  reader can be wrong and the person has the paper in front of
                  them. Confirm carries this and the rows together. */}
              {reviewHeader && (
                <PoHeaderFields
                  header={reviewHeader}
                  warnings={reviewWarnings}
                  testIdPrefix="po-review-header"
                  poDate={reviewMeta.po_date}
                  currency={reviewMeta.currency}
                  onChange={(key, value) =>
                    setReviewHeader((cur) => (cur ? { ...cur, [key]: value } : cur))}
                />
              )}

              {parsed.rows.length === 0 && (
                <div data-testid="po-paste-empty" style={{
                  fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
                }}>
                  Nothing could be read from that text.
                </div>
              )}

              {parsed.rows.map((row) => {
                const draft = draftFromParsed(row);
                const edit = (field: keyof Draft, value: string) =>
                  setReviewEdits((cur) => ({
                    ...cur, [row.index]: { ...(cur[row.index] ?? {}), [field]: value },
                  }));
                // ⚠️ THE CARRIED FIELDS SIT ON A SECOND LINE, not squeezed
                // into the first. Nine boxes in one row inside a 720px panel
                // makes each one too narrow to read a part number in, and a
                // review table you cannot read is not a review.
                const carried: [keyof Draft, string, number][] = [
                  ["line_number", "Line no.", 70],
                  ["uom", "UOM", 70],
                  ["extended_price", "Extended", 110],
                  ["anixter_part_number", supplierCodeLabel, 150],
                ];
                return (
                  <div
                    key={row.index}
                    data-testid={`po-paste-row-${row.index}`}
                    style={{ marginBottom: spacing[8] }}
                  >
                  <div className="flex items-center" style={{ gap: spacing[6] }}>
                    <span style={{
                      width: 20, fontSize: typography.fontSize.xs,
                      color: semanticColors.textMuted, fontVariantNumeric: "tabular-nums",
                    }}>
                      {row.index}
                    </span>
                    {([
                      ["part_number", row.partNumber, 190, "Part number"],
                      ["quantity", row.quantity, 80, "Qty"],
                      ["unit_price", row.unitPrice, 110, "Unit price"],
                      ["manufacturer", row.manufacturer, 120, "Make"],
                      ["description", row.description, 200, "Description"],
                    ] as const).map(([field, cell, width, label]) => (
                      <input
                        key={field}
                        data-testid={`po-paste-${field}-${row.index}`}
                        value={draft[field]}
                        onChange={(e) => edit(field, e.target.value)}
                        placeholder={cell.kind === "unreadable" ? UNREADABLE_LABEL : label}
                        // ⚠️ THE PLACEHOLDER IS THE WORD "UNREADABLE", NOT A 0
                        // AND NOT A GUESS. The input is EMPTY, so the existing
                        // save check still refuses the line until a person
                        // supplies the figure.
                        title={cell.kind === "unreadable" ? cell.reason : undefined}
                        style={{
                          width, height: heights.input, padding: `0 ${spacing[6]}px`,
                          fontSize: typography.fontSize.sm,
                          border: `1px solid ${
                            cell.kind === "unreadable" ? colors.amber600 : semanticColors.border
                          }`,
                          borderRadius: radius.base,
                        }}
                      />
                    ))}
                    {row.flagged && (
                      <span
                        data-testid={`po-paste-flag-${row.index}`}
                        title={row.flagReason}
                        style={{
                          height: heights.pill, padding: `0 ${spacing[8]}px`,
                          display: "inline-flex", alignItems: "center",
                          backgroundColor: colors.indigo50, color: semanticColors.accentTextOnTint,
                          border: `1px solid ${colors.indigo100}`,
                          borderRadius: radius.base,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.medium,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Check this row
                      </span>
                    )}
                  </div>

                  {/* ⚠️ THE SECOND LINE: what the document printed that the
                      five boxes above have no room for, plus the unit price
                      AS PRINTED. Every one of these rides the save. */}
                  <div className="flex items-center"
                       style={{ gap: spacing[6], marginTop: spacing[4],
                                paddingLeft: 26, flexWrap: "wrap" }}>
                    {carried.map(([field, label, width]) => (
                      <label key={field} style={{
                        display: "flex", alignItems: "center", gap: spacing[4],
                        fontSize: typography.fontSize.xs,
                        color: semanticColors.textMuted,
                      }}>
                        {label}
                        <input
                          data-testid={`po-paste-${field}-${row.index}`}
                          value={(draft[field] as string | undefined) ?? ""}
                          onChange={(e) => edit(field, e.target.value)}
                          placeholder="—"
                          style={{
                            width, height: heights.input,
                            padding: `0 ${spacing[6]}px`,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${semanticColors.border}`,
                            borderRadius: radius.base,
                          }}
                        />
                      </label>
                    ))}
                    {/* ⚠️ EVERY PRINTED DECIMAL, SHOWN. The editable box above
                        holds the PARSED number because Number("10,322.0000")
                        is NaN and the save check would refuse a figure that
                        was read perfectly well. This is the document's own
                        text, shown whenever it differs, so a person checking
                        against the paper sees exactly what the paper says --
                        and it is what the record stores as unit_price_text. */}
                    {draft.unit_price_text
                      && draft.unit_price_text !== draft.unit_price && (
                      <span
                        data-testid={`po-paste-unit-as-printed-${row.index}`}
                        style={{
                          fontSize: typography.fontSize.xs,
                          color: semanticColors.textMuted,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        Unit price as printed: {draft.unit_price_text}
                      </span>
                    )}
                  </div>

                  {/* ⚠️ SEVERAL SHIPMENTS: THE QUANTITY IS LEFT FOR A PERSON
                      AND EVERY SHIPMENT IS LISTED. Never summed -- a sum is a
                      figure printed on no page of the document. Amber, and it
                      blocks nothing; the empty quantity is what the existing
                      save check already refuses until it is filled in. */}
                  {draft.shipments_note && (
                    <div
                      data-testid={`po-paste-shipments-${row.index}`}
                      style={{
                        marginTop: spacing[4], marginLeft: 26,
                        padding: `${spacing[6]}px ${spacing[8]}px`,
                        backgroundColor: colors.amber50,
                        border: `1px solid ${colors.amber200}`,
                        borderRadius: radius.base,
                        fontSize: typography.fontSize.xs,
                        color: semanticColors.textPrimary,
                      }}
                    >
                      {draft.shipments_note}
                    </div>
                  )}
                  </div>
                );
              })}

              <div className="flex items-center" style={{ gap: spacing[8], marginTop: spacing[10] }}>
                <button
                  data-testid="po-paste-confirm"
                  className="btn-primary"
                  disabled={parsed.rows.length === 0}
                  onClick={onConfirmPaste}
                  style={{
                    height: heights.control, padding: `0 ${spacing[14]}px`,
                    backgroundColor: parsed.rows.length === 0 ? colors.gray200 : colors.indigo600,
                    color: colors.white, border: "none", borderRadius: radius.base,
                    fontSize: typography.fontSize.base,
                    cursor: parsed.rows.length === 0 ? "not-allowed" : "pointer",
                  }}
                >
                  Add {parsed.rows.length} line{parsed.rows.length === 1 ? "" : "s"}
                </button>
                {/* Only the paste path has a "text" to go back to. */}
                {reviewSource === "paste" && (
                  <button data-testid="po-paste-back" onClick={() => setPasteStage("pasting")} style={SECONDARY_BUTTON}>
                    Back to the text
                  </button>
                )}
                <button data-testid="po-paste-discard" onClick={onCancelPaste} style={SECONDARY_BUTTON}>
                  Discard
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center" style={{ gap: spacing[8] }}>
            <button
              data-testid="po-line-add"
              onClick={() => setRows([...rows, { ...EMPTY_ROW }])}
              style={{
                height: heights.control, padding: `0 ${spacing[12]}px`,
                display: "flex", alignItems: "center", gap: spacing[6],
                backgroundColor: semanticColors.surface,
                color: semanticColors.textSecondary,
                border: `1px solid ${semanticColors.border}`,
                borderRadius: radius.base,
                fontSize: typography.fontSize.base, cursor: "pointer",
              }}
            >
              <Plus size={iconSize.sm} strokeWidth={iconStroke.regular} />
              Add line
            </button>
            {/* ⚠️ BESIDE "Add line", NOT INSTEAD OF IT. Typing one line by hand
                stays exactly as it was; this is the fast path for twenty. */}
            <button
              data-testid="po-paste-open"
              onClick={() => { setPasteStage("pasting"); setPasteText(""); }}
              style={{
                height: heights.control, padding: `0 ${spacing[12]}px`,
                display: "flex", alignItems: "center", gap: spacing[6],
                backgroundColor: semanticColors.surface,
                color: semanticColors.textSecondary,
                border: `1px solid ${semanticColors.border}`,
                borderRadius: radius.base,
                fontSize: typography.fontSize.base, cursor: "pointer",
              }}
            >
              <ClipboardPaste size={iconSize.sm} strokeWidth={iconStroke.regular} />
              Paste lines
            </button>

            {/* ===== EXTRACT: only where a PDF is attached AND the user may
                write. Absent otherwise -- not greyed. ================== */}
            {canWrite && docs.length > 0 && (
              <>
                {/* ⚠️ MORE THAN ONE PDF IS A CHOICE, AND THIS PANEL DOES NOT
                    MAKE IT -- the same call the auto-load makes about more
                    than one recorded PO. Picking the most recent would be a
                    plausible guess that is wrong exactly when it matters. */}
                {docs.length > 1 && (
                  <select
                    data-testid="po-extract-pick"
                    value={chosenDoc ?? ""}
                    onChange={(e) => setChosenDoc(e.target.value ? Number(e.target.value) : null)}
                    style={{
                      height: heights.control, padding: `0 ${spacing[8]}px`,
                      border: `1px solid ${semanticColors.border}`,
                      borderRadius: radius.base, backgroundColor: semanticColors.surface,
                      fontSize: typography.fontSize.base, maxWidth: 220,
                    }}
                  >
                    <option value="">Choose a PDF…</option>
                    {docs.map((d) => (
                      <option key={d.id} value={d.id}>{d.filename}</option>
                    ))}
                  </select>
                )}
                <button
                  data-testid="po-extract-open"
                  disabled={extracting || (docs.length > 1 && chosenDoc === null)}
                  onClick={() => void onExtract(docs.length === 1 ? docs[0].id : (chosenDoc as number))}
                  title={docs.length === 1 ? `Read ${docs[0].filename}` : undefined}
                  style={{
                    height: heights.control, padding: `0 ${spacing[12]}px`,
                    display: "flex", alignItems: "center", gap: spacing[6],
                    backgroundColor: semanticColors.surface,
                    color: semanticColors.textSecondary,
                    border: `1px solid ${semanticColors.border}`,
                    borderRadius: radius.base,
                    fontSize: typography.fontSize.base,
                    cursor: extracting ? "wait" : "pointer",
                    opacity: extracting || (docs.length > 1 && chosenDoc === null) ? 0.6 : 1,
                  }}
                >
                  <FileSearch size={iconSize.sm} strokeWidth={iconStroke.regular} />
                  {extracting ? "Reading…" : "Extract from PDF"}
                </button>
              </>
            )}
            <button
              data-testid="po-compare-run"
              className="btn-primary"
              disabled={busy}
              onClick={() => void onSave()}
              style={{
                height: heights.control, padding: `0 ${spacing[14]}px`,
                backgroundColor: colors.indigo600, color: semanticColors.onAccent,
                border: "none", borderRadius: radius.base,
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.medium,
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? "Comparing…" : "Save PO and compare"}
            </button>
          </div>
        </div>
      )}

      {/* ⚠️ A VIEWER SEES THE LOADED PO AS TEXT, NOT AS DISABLED INPUTS. The
          write form is Sales/Admin's; offering a Viewer inputs that refuse
          every keystroke reads as a broken form rather than a read-only one. */}
      {!canWrite && loadedPo && (
        <div data-testid="po-loaded-readonly" style={{ marginBottom: spacing[12] }}>
          {loadedPo.lines.map((l, i) => (
            <div key={l.id} data-testid={`po-loaded-line-${i}`}
                 className="flex items-center"
                 style={{ gap: spacing[8], padding: `${spacing[4]}px 0`,
                          fontSize: typography.fontSize.sm }}>
              <span style={{ flex: 2 }}>{l.part_number}</span>
              <span style={{ flex: 1, color: semanticColors.textSecondary }}>
                Qty {l.quantity}
              </span>
              <span style={{ flex: 1, color: semanticColors.textSecondary }}>
                Unit price {formatRupees(l.unit_price)}
              </span>
              {/* A Viewer sees the same fact as everyone else. */}
              <LineOriginTag origin={l.line_origin}
                             testId={`po-loaded-line-origin-${i}`} />
            </div>
          ))}
        </div>
      )}

      {status === "ok" && pos.length > 0 && (
        <div style={{ marginBottom: spacing[8] }}>
          {pos.map((po) => (
            <div key={po.id}>
            <div data-testid={`po-record-${po.id}`}
                 className="flex items-center"
                 style={{ gap: spacing[8], padding: `${spacing[6]}px 0`,
                          borderTop: `1px solid ${semanticColors.border}`,
                          fontSize: typography.fontSize.sm }}>
              <span style={{ fontWeight: typography.fontWeight.medium, flex: 1 }}>
                {po.po_reference}
              </span>
              <span style={{ color: semanticColors.textSecondary }}>
                {po.lines.length} {po.lines.length === 1 ? "line" : "lines"}
              </span>
              {/* WHO, as recorded from the session. "unknown" for a PO from
                  before attribution existed -- never a guess. */}
              <span data-testid={`po-record-actor-${po.id}`}
                    style={{ color: semanticColors.textMuted }}>
                Recorded by {po.created_by ?? "unknown"}
                {po.last_edited_by && po.last_edited_by !== po.created_by
                  ? ` · last edited by ${po.last_edited_by}` : ""}
              </span>
              {/* Open is how a person CHOOSES when there is more than one.
                  It loads the form and writes nothing. */}
              {po.id !== loadedPoId && (
                <button
                  data-testid={`po-record-open-${po.id}`}
                  onClick={() => loadPo(po)}
                  disabled={busy}
                  style={SECONDARY_BUTTON}
                >
                  Open
                </button>
              )}
              <button
                data-testid={`po-record-compare-${po.id}`}
                onClick={() => void onCompareExisting(po)}
                disabled={busy}
                style={SECONDARY_BUTTON}
              >
                Compare
              </button>
              {canWrite && (
                <button
                  data-testid={`po-record-delete-${po.id}`}
                  aria-label={`Remove PO ${po.po_reference}`}
                  aria-expanded={confirmDeleteId === po.id}
                  // Opens the confirmation. It does NOT delete.
                  onClick={() => setConfirmDeleteId(po.id)}
                  disabled={busy}
                  style={{
                    width: iconSize.lg, height: iconSize.lg, padding: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: "transparent", border: "none",
                    color: semanticColors.textPlaceholder, cursor: "pointer",
                  }}
                >
                  <Trash2 size={iconSize.sm} strokeWidth={iconStroke.regular} />
                </button>
              )}
            </div>
            {/* THE CONFIRMATION. Names the PO and what goes with it -- a
                generic "are you sure" is the one that gets clicked through. */}
            {canWrite && confirmDeleteId === po.id && (
              <div
                data-testid={`po-record-delete-confirm-${po.id}`}
                role="alertdialog"
                aria-label={`Confirm deleting PO ${po.po_reference}`}
                className="flex items-center"
                style={{
                  gap: spacing[8], flexWrap: "wrap", marginBottom: spacing[6],
                  padding: `${spacing[6]}px ${spacing[8]}px`, borderRadius: radius.base,
                  backgroundColor: colors.red50, border: `1px solid ${colors.red100}`,
                  fontSize: typography.fontSize.sm, color: colors.red600,
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  Delete PO <strong>{po.po_reference}</strong> and its {po.lines.length} typed{" "}
                  {po.lines.length === 1 ? "line" : "lines"}? This cannot be undone.
                </span>
                <button
                  data-testid={`po-record-delete-cancel-${po.id}`}
                  onClick={() => setConfirmDeleteId(null)}
                  disabled={busy}
                  style={SECONDARY_BUTTON}
                >
                  Cancel
                </button>
                <button
                  data-testid={`po-record-delete-yes-${po.id}`}
                  onClick={() => void onDelete(po)}
                  disabled={busy}
                  style={{ ...SECONDARY_BUTTON, backgroundColor: colors.red600,
                           color: colors.white, border: "none" }}
                >
                  Delete PO
                </button>
              </div>
            )}
            </div>
          ))}
        </div>
      )}

      {/* ⚠️ A REFUSAL IS NOT A RESULT. Two known, different currencies mean
          nothing was compared at all -- so the verdict table is not rendered
          beside the message, which would imply the rows below were somehow
          still valid. */}
      {comparison?.refused && (
        <div
          data-testid="po-comparison-refused"
          style={{
            fontSize: typography.fontSize.sm, color: semanticColors.accentTextOnTint,
            backgroundColor: colors.indigo50, border: `1px solid ${colors.indigo100}`,
            borderRadius: radius.base, padding: spacing[10],
          }}
        >
          {comparison.refusal_reason}
        </div>
      )}

      {comparison && !comparison.refused && (
        <div data-testid="po-comparison-result">
          <div style={{ fontSize: typography.fontSize.sm,
                        color: semanticColors.textSecondary,
                        marginBottom: spacing[6] }}>
            {comparison.po_reference} — comparing unit prices
            {comparison.quote_currency ? ` in ${comparison.quote_currency}` : ""}
          </div>
          {comparison.rows.map((row, i) => (
            <div key={i} data-testid={`po-comparison-row-${i}`}
                 className="flex items-center"
                 style={{ gap: spacing[8], padding: `${spacing[6]}px 0`,
                          borderTop: `1px solid ${semanticColors.border}`,
                          fontSize: typography.fontSize.sm }}>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden",
                             textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {row.part_number}
              </span>
              {/* ⚠️ BOTH SIDES, ALWAYS LABELLED. A verdict without the two
                  figures sends somebody back to the paper, and an unlabelled
                  figure invites reading a unit price as a line total. */}
              <span style={{ color: semanticColors.textSecondary, whiteSpace: "nowrap" }}>
                PO {row.po_quantity ?? "—"} @ {row.po_unit_price != null ? formatRupees(row.po_unit_price) : "—"}
              </span>
              <span style={{ color: semanticColors.textSecondary, whiteSpace: "nowrap" }}>
                Quoted {row.quote_quantity ?? "—"} @ {row.quote_unit_price != null ? formatRupees(row.quote_unit_price) : "—"}
              </span>
              <VerdictPill verdict={row.verdict} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
